import root from 'window-or-global';
import {isNeverMode, buildCanonicalUrl, buildHreflangTags, buildOgUrl} from 'epic-ue-locale-mode';
import React from 'react';
import {Helmet} from 'react-helmet';
import {getTagsFromModel} from '@epic-mw/common-utils';
import {LocalizationContext} from '@epic-core/localization';
import {getLocale} from '@epic-core/common';
import parse from 'url-parse';

const EPIC_GAMES = 'Epic Games';

const defaultImg =
    'https://cdn2.unrealengine.com/epic-games-social-share-1200x675-7670432fc58a.png';

export const getBasicMetaTags = () => {
    return [
        {
            name: 'twitter:site',
            content: '@epicgames'
        },
        {
            property: 'og:site_name',
            content: EPIC_GAMES
        },
        {
            name: 'twitter:card',
            content: 'summary_large_image'
        },
        {
            property: 'og:type',
            content: 'article'
        }
    ];
};

// Manipulates the title string passed in to remove "Epic Games | " and append " - Epic Games"
export const titleAppendEpicGames = (title) => {
    if (!title) return '';

    let updatedTitle = title.replace(/^Epic Games \| /i, '');
    updatedTitle = updatedTitle += ' - Epic Games';
    return updatedTitle;
};

export const getMetaTags = (getMessage, title, desc, img) => {
    let description = '';
    const fbImage = img;
    const twImage = img;
    const locale = getLocale();
    if (desc) {
        //escape html tags
        description = desc.replace(/(<([^>]+)>)/gi, '');
    } else {
        description = 'We develop cutting-edge games and cross-platform game engine technology!';
    }

    const metaLocale = locale ? locale.replace('-', '_') : locale;
    const metaTitle = title || EPIC_GAMES;

    const tags = [
        // all og tags need to be "property"
        {property: 'og:locale', content: metaLocale},
        {property: 'og:description', content: description},
        {property: 'og:image', content: fbImage},
        {property: 'og:title', content: metaTitle},

        // All twitter meta tags needs to be "name"
        // Description needs to be "name"
        {name: 'description', content: description},
        {name: 'twitter:description', content: description},
        {name: 'twitter:image', content: twImage},
        {name: 'twitter:title', content: metaTitle}
    ];

    return tags.concat(getBasicMetaTags(getMessage));
};

export const getHelmetForMetaTags = (pageModel) => {
    if (!pageModel) return null;

    const {tags, title} = getTagsFromModel(pageModel);

    let hasImage;
    let hasTitle;
    let hasDescription;
    let hasOpenGraphDescription;
    let hasLocale;
    let hasUrl;

    const currentUrl = parse(root.location.href);
    currentUrl.set('hash', '');
    currentUrl.set('query', '');

    const neverMode = isNeverMode();
    const locale = getLocale();
    const baseUrl = `${currentUrl.protocol}//${currentUrl.host}`;
    const pathname = currentUrl.pathname;

    tags.forEach((tag) => {
        if (tag && tag.name === 'description') {
            hasDescription = true;
        } else if (tag && tag.property === 'og:description') {
            hasOpenGraphDescription = true;
        } else if (tag && tag.property === 'og:title') {
            hasTitle = true;
        } else if (tag && tag.property === 'og:image') {
            hasImage = true;
        } else if (tag && tag.property === 'og:locale') {
            hasLocale = true;
        } else if (tag && tag.property === 'og:url') {
            hasUrl = true;
            tag.content = neverMode ? buildOgUrl(baseUrl, pathname) : currentUrl.toString();
        }
    });

    if (!hasLocale) {
        const metaLocale = locale ? locale.replace('-', '_') : locale;
        tags.push({
            property: 'og:locale',
            content: metaLocale
        });
    }

    if (!hasImage) {
        tags.push({
            property: 'og:image',
            content: defaultImg
        });
        tags.push({
            name: 'twitter:image',
            content: defaultImg
        });
    }

    const canonicalHref = neverMode
        ? buildCanonicalUrl(baseUrl, pathname, locale)
        : currentUrl.toString();

    const links = [
        {
            rel: 'canonical',
            href: canonicalHref
        }
    ];

    if (!hasUrl) {
        tags.push({
            property: 'og:url',
            content: neverMode ? buildOgUrl(baseUrl, pathname) : currentUrl.toString()
        });
    }

    return (
        <LocalizationContext.Consumer>
            {(context) => {
                const meta = tags.concat(getBasicMetaTags(null, context.messages));
                const metaTitle = title || context.messages['epic.ue.social.meta.title'];
                const appendedTitle = titleAppendEpicGames(metaTitle);

                if (!hasTitle) {
                    meta.push({
                        property: 'og:title',
                        content: metaTitle
                    });
                    meta.push({
                        name: 'twitter:title',
                        content: metaTitle
                    });
                }

                if (!hasDescription) {
                    meta.push({
                        name: 'description',
                        content: context.messages['epic.ue.social.meta.description']
                    });
                    meta.push({
                        name: 'twitter:description',
                        content: context.messages['epic.ue.social.meta.description']
                    });
                }

                if (!hasOpenGraphDescription) {
                    meta.push({
                        property: 'og:description',
                        content: context.messages['epic.ue.social.meta.description']
                    });
                }

                const allLinks = [...links];
                if (neverMode && context.allLocales && context.allLocales.length) {
                    const hreflangTags = buildHreflangTags(baseUrl, pathname, context.allLocales);
                    hreflangTags.forEach((hreflangTag) => {
                        allLinks.push({
                            rel: 'alternate',
                            hreflang: hreflangTag.hreflang,
                            href: hreflangTag.href
                        });
                    });
                }

                return <Helmet link={allLinks} meta={meta} title={appendedTitle} />;
            }}
        </LocalizationContext.Consumer>
    );
};
